import { watch } from "vue";

const MARKER_KEY = "lesson";
const MARKER_VALUE = "1";

/**
 * Mirrors "a lesson chat is open" into the route as a `?lesson=1` query marker
 * so the system back gesture / hardware back, browser back, and the "My Course"
 * nav link all return the user to the course overview.
 *
 * Route and router are injected (rather than read via useRoute/useRouter inside)
 * so this can be unit-tested without a vue-router injection context.
 *
 * @param {object} opts
 * @param {import('vue').Ref<string|null>} opts.dialogId - reactive open-chat id
 * @param {import('vue-router').RouteLocationNormalizedLoaded} opts.route
 * @param {import('vue-router').Router} opts.router
 * @param {() => void} opts.onExit - run when the marker is removed while a chat is open
 * @returns {{ openMarker: () => void, stripMarker: () => void }}
 */
export function useLessonHistoryMarker({ dialogId, route, router, onExit }) {
  const hasMarker = () => route.query[MARKER_KEY] != null;

  // Add a back-able history entry when a lesson opens.
  const openMarker = () => {
    if (hasMarker()) return;
    router.push({
      path: route.path,
      query: { ...route.query, [MARKER_KEY]: MARKER_VALUE },
    });
  };

  // Remove the marker from the URL without adding history (used on exit).
  const stripMarker = () => {
    if (!hasMarker()) return;
    const query = { ...route.query };
    delete query[MARKER_KEY];
    router.replace({ path: route.path, query });
  };

  watch(
    () => route.query[MARKER_KEY],
    (marker, prevMarker) => {
      if (marker == null && prevMarker != null && dialogId.value) {
        // Back / My Course removed the marker while a chat was open → exit.
        onExit();
      } else if (marker != null && !dialogId.value) {
        // Stale marker (forward-nav, or My-Course-then-back) → clean the URL.
        stripMarker();
      }
    }
  );

  return { openMarker, stripMarker };
}
